import Decimal from "decimal.js";

import { deep_clone_simple } from "../constants/utils";
import { default_property } from "../schema/investment_property";
import { RETAINED, INVESTMENT, PRIMARY, SECONDARY } from "../constants/property_disposition";
import {
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  VACATION_HOME,
  INVESTMENT_PROPERTY as INVESTMENT_CLASSIFICATION
} from "../constants/property_classification";
import { YES, NO } from "../constants/yes_no";
import { first_current_monthly_payment, amount_frequency_monthly } from "./property";
import { MONTHLY } from "../constants/frequencies";

/**
 * Maps the refi Subject Property's "What Property are you Refinancing" type
 * (constants/property_classification) to the Invest REO occupancy value
 * (constants/property_disposition) that drives the Net Income/Loss N/A rule and
 * the reserves tier. Returns null for an unset/unknown type so the sync leaves
 * the row untouched rather than forcing a value.
 */
export function subject_occupancy_from_classification(classification) {
  if (classification === PRIMARY_RESIDENCE) return PRIMARY;
  if (classification === SECOND_HOME || classification === VACATION_HOME) return SECONDARY;
  if (classification === INVESTMENT_CLASSIFICATION) return INVESTMENT;
  return null;
}

/**
 * Keeps the subject-property Invest REO row's occupancy (intended + current) in
 * lockstep with the Subject Property page's chosen type, so the retained Primary
 * Residence / Second Home automatically reads N/A for Net Income or Loss (and is
 * kept out of DTI + tiered correctly for reserves), while an Investment subject
 * calculates normally — the LO sets the type once on the Subject Property page.
 *
 * Returns a new investment_properties array (or the same reference if nothing
 * changed). Call wherever the document is committed (Applicant Info + Subject
 * Property). Only the single is_subject_property row is touched; LO-entered REO
 * rows are never modified.
 */
export function sync_subject_property_reo_occupancy(document) {
  const properties = document.investment_properties || [];
  const classification = document.property && document.property.classification;
  const occupancy = subject_occupancy_from_classification(classification);
  if (occupancy === null) {
    return properties;
  }
  let changed = false;
  const updated = properties.map(p => {
    if (
      p.is_subject_property === YES &&
      (p.intended_occupancy !== occupancy || p.current_occupancy !== occupancy)
    ) {
      changed = true;
      return { ...p, intended_occupancy: occupancy, current_occupancy: occupancy };
    }
    return p;
  });
  return changed ? updated : properties;
}

// Marks the single Invest REO entry that LANIS manages automatically for a
// current residence the borrower is retaining as an investment. LANIS only ever
// adds / updates / removes the tagged entry - it never touches REO rows the LO
// entered by hand.
export const AUTO_RETAINED_REO_TAG = "auto_from_retained_residence";

function isRetainedInvestment(borrowers) {
  const primary = (borrowers || [])[0];
  return (
    !!primary &&
    primary.residence_disposition_status === RETAINED &&
    primary.residence_intended_occupancy === INVESTMENT
  );
}

/**
 * Returns a new investment_properties array reflecting the retained current
 * residence. When the primary borrower keeps their home as an investment, a
 * single tagged Invest REO entry is upserted from the Selling Property page
 * (document.property_to_be_sold) + the borrower's current address; otherwise the
 * tagged entry is removed. LO-entered (untagged) REO rows are preserved as-is.
 *
 * Call this wherever borrowers or property_to_be_sold are committed to the
 * document (Applicant Info + Selling Property) so the REO list - and therefore
 * the reserve requirement - stays in sync across the Reserves page, the Summary
 * Sheet, and the MISMO export.
 */
export function sync_retained_residence_reo(document) {
  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const all = document.investment_properties || [];
  const untagged = all.filter(p => !p[AUTO_RETAINED_REO_TAG]);

  if (!isRetainedInvestment(borrowers)) {
    return untagged;
  }

  const pts = document.property_to_be_sold || {};
  const primary = borrowers[0];
  // Preserve any LO edits to the auto row that we don't source (rent, property
  // type, tax returns); core financials always mirror the Selling Property page.
  const prior = all.find(p => p[AUTO_RETAINED_REO_TAG]) || {};

  const entry = {
    ...deep_clone_simple(default_property),
    ...prior,
    [AUTO_RETAINED_REO_TAG]: YES,
    is_subject_property: NO,
    disposition_status: RETAINED,
    intended_occupancy: INVESTMENT,
    current_occupancy: INVESTMENT,
    owners: borrowers.map((_b, i) => String(i)),
    property_address: primary.current_address || "",
    property_address_2: primary.current_address_2 || "",
    property_city: primary.city || "",
    property_state: primary.state || "",
    property_zip: primary.zip_code || "",
    market_value: String(primary.market_value || "0"),
    principal_balance: new Decimal(pts.first_loan_balance || 0)
      .add(pts.second_loan_balance || 0)
      .add(pts.third_loan_balance || 0)
      .toString(),
    first_mortgage: first_current_monthly_payment(pts).toString(),
    second_mortgage: String(pts.second_current_monthly_payment || "0"),
    taxes: amount_frequency_monthly(
      pts.present_property_tax,
      pts.present_property_tax_frequency
    ).toString(),
    insurance: amount_frequency_monthly(
      pts.present_home_insurance,
      pts.present_home_insurance_frequency
    ).toString(),
    hoa1: String(pts.present_hoa1 || "0"),
    hoa1_frequency: pts.present_hoa1_frequency || "",
    hoa2: String(pts.present_hoa2 || "0"),
    hoa2_frequency: pts.present_hoa2_frequency || ""
  };

  return [...untagged, entry];
}

/**
 * Reverse of the above: when the LO edits the tagged auto REO entry on the
 * Invest REO page, mirror its dollar amounts back onto the Selling Property page
 * (document.property_to_be_sold) so the two stay in agreement. Monthly amounts
 * map back at Monthly frequency; the combined P&I lands in principal and the
 * combined UPB in the 1st balance (totals preserved, forward-sync stable).
 * Only call this for the tagged auto entry.
 */
export function reverse_sync_reo_to_selling_property(document, reo) {
  const pts = { ...(document.property_to_be_sold || {}) };
  pts.first_current_monthly_payment_principal = String(reo.first_mortgage || "0");
  pts.first_current_monthly_payment_interest = "0";
  pts.second_current_monthly_payment = String(reo.second_mortgage || "0");
  pts.present_property_tax = String(reo.taxes || "0");
  pts.present_property_tax_frequency = MONTHLY;
  pts.present_home_insurance = String(reo.insurance || "0");
  pts.present_home_insurance_frequency = MONTHLY;
  pts.present_hoa1 = String(reo.hoa1 || "0");
  pts.present_hoa1_frequency = reo.hoa1_frequency || "";
  pts.present_hoa2 = String(reo.hoa2 || "0");
  pts.present_hoa2_frequency = reo.hoa2_frequency || "";
  pts.first_loan_balance = String(reo.principal_balance || "0");
  pts.second_loan_balance = "0";
  pts.third_loan_balance = "0";
  return pts;
}
